import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gtagEvent } from 'src/utils/gtag';
import { filterListBy, getReserveTypeCategory } from 'src/utils/helpers/filter';
import useRestApiClient from 'src/hooks/useRestApiClient';

const initialState = {
  filteredApps: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { category: '', industry: '', search_title: '', sortby: '' },
  setFilterValues: () => Promise.resolve()
};

const AppsContext = createContext(initialState);

export const AppsProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [isPreloaded, setPreloaded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const client = useRestApiClient();

  const removeFromList = (appId) => {
    setApps((prev) => {
      const filtered = prev.filter((row) => row.id !== appId);
      return [...filtered];
    });
  };

  const filterAppCategory = (list, categoryFilter) => {
    if (!categoryFilter) return list;
    return list.filter((item) => {
      if (!getReserveTypeCategory(item.category)) return false;
      const haystack = getReserveTypeCategory(item.category)
        .trim()
        .toLowerCase();

      return (
        haystack.includes(categoryFilter.trim().toLowerCase()) && item.visible
      );
    });
  };

  const loadApps = async (force = false) => {
    if (apps.length > 0 && !force) return;
    if (isLoading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/features');
      setApps(message);
      setFilteredApps(message);
      setPreloaded(true);
    } catch (error) {
      setApps([]);
      setFilteredApps([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const reloadApps = async () => {
    setLoading(true);
    const {
      data: { message }
    } = await client.get('/features');
    setApps(message);
    setLoading(false);
  };

  useEffect(() => {
    let filterApps = apps;
    const {
      category,
      industry,
      sortby,
      search_title: searchTitle
    } = filterValues;

    // make sure to only returns published records
    filterApps =
      filterApps.length > 0 &&
      filterApps.filter((record) => +record.publish === 1);

    filterApps = filterAppCategory(filterApps, category);
    filterApps = filterListBy(filterApps, 'industry', industry);

    if (sortby) {
      let sortedApps = [...filterApps];
      switch (sortby) {
        case 'latest':
          sortedApps = filterApps.sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
          });
          break;
        case 'popular':
          sortedApps = filterApps.sort((a, b) => {
            const firstUsage = typeof a.usage !== 'undefined' ? a.usage : 0;
            const secondUsage = typeof b.usage !== 'undefined' ? b.usage : 0;
            return secondUsage - firstUsage;
          });
          break;
        default:
          // no default option
          break;
      }
      filterApps = [...sortedApps];
    }

    if (searchTitle && searchTitle.length >= 3) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterApps = filterApps.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredApps(filterApps);
  }, [filterValues, apps]);

  const values = useMemo(
    () => ({
      isPreloaded,
      isLoading,
      filterValues,
      apps,
      filteredApps,
      removeFromList,
      setFilterValues,
      loadApps,
      reloadApps
    }),
    [isPreloaded, isLoading, filterValues, apps, filteredApps]
  );

  return (
    <AppsContext.Provider value={values} {...contextProviderProps}>
      {children}
    </AppsContext.Provider>
  );
};

AppsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppsContext;
